<template>
  <div class="group">
    <template v-if="groups.length">
      <b-tooltip v-if="isTruncated" v-bind="tooltipOptionDefault" />
      <span ref="truncTarget" class="text ellipsis">
        {{ defaultGroupName }}
      </span>
      <template v-if="otherGroupNames.length">
        <span class="text">,</span>
        <span class="text others" v-b-tooltip="tooltipOptionOthers">
          {{
            $tc('appOrganizationCellRenderer.others', otherGroupNames.length)
          }}
        </span>
      </template>
    </template>
    <span v-else class="text">-</span>
  </div>
</template>

<script>
import { useIsTruncated } from '@/composables/UseIsTruncated'

/**
 * ag-grid の cell に所属グループを表示するレンダラー
 */
export default {
  name: 'AppOrganizationCellRenderer',
  setup() {
    const { truncTarget, isTruncated } = useIsTruncated()
    return { truncTarget, isTruncated }
  },
  computed: {
    /**
     * グループデータ
     * @type {Object[]}
     */
    groups() {
      return this.params.value
    },
    /**
     * デフォルト扱いするグループのデータ
     * @type {Object}
     */
    defaultGroup() {
      if (!this.groups?.length) return null
      return this.groups.find((g) => g.default_organization) || this.groups[0]
    },
    /**
     * デフォルト表示するグループの名称
     * @type {string}
     */
    defaultGroupName() {
      return this.defaultGroup?.organization_name || ''
    },
    /**
     * デフォルト表示しないグループの名称
     * @type {string[]}
     */
    otherGroupNames() {
      if (!this.defaultGroup) return []
      const defaultGroupId =
        this.defaultGroup.organization_id || this.defaultGroup.id
      return this.groups
        .filter((g) => (g.organization_id || g.id) !== defaultGroupId)
        .map((g) => g.organization_name)
    },
    /**
     * デフォルトグループ側のツールチップオプション
     * @type {Object}
     */
    tooltipOptionDefault() {
      return {
        target: () => this.truncTarget,
        interactive: false,
        container: 'body',
        boundary: 'window',
        placement: 'bottom',
        customClass: 'ifield-tooltip',
        title: this.defaultGroupName,
      }
    },
    /**
     * その他グループ側のツールチップオプション
     * @type {Object}
     */
    tooltipOptionOthers() {
      return {
        title: this.otherGroupNames.join(', '),
        placement: 'right',
        boundary: 'window',
        container: 'body',
        customClass: 'ifield-tooltip-light',
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../assets/scss/mixin' as mixin;

.group {
  max-width: 100%;
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;

  .text {
    &.ellipsis {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &.others {
      flex-shrink: 0;
      margin-left: 4px;
      text-decoration: underline;
      cursor: pointer;
    }
  }
}

.other-tooltip {
  &.tooltip {
    opacity: 1;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.24));
  }

  &.bs-tooltip-bottom {
    ::v-deep .arrow {
      &::before {
        border-bottom-color: #ffffff;
      }
    }
  }
  &.bs-tooltip-top {
    ::v-deep .arrow {
      &::before {
        border-top-color: #ffffff;
      }
    }
  }
  &.bs-tooltip-left {
    ::v-deep .arrow {
      &::before {
        border-left-color: #ffffff;
      }
    }
  }
  &.bs-tooltip-right {
    ::v-deep .arrow {
      &::before {
        border-right-color: #ffffff;
      }
    }
  }

  ::v-deep .tooltip-inner {
    // 16.5文字 + 両側 padding 値
    max-width: calc(#{$font-size-middle} * 16.5 + 32px);
    // 11.5文字 + 上側 padding 値
    max-height: calc(#{$font-size-middle} * 1.5 * 11.5 + 8px);
    padding: 8px;
    text-align: left;
    font-size: $font-size-middle;
    color: $text-color;
    background-color: #ffffff;
    overflow-y: auto;
    @include mixin.scrollbar;
    &::-webkit-scrollbar-track {
      border-top-right-radius: $border-radius-small;
      border-bottom-right-radius: $border-radius-small;
    }
    & {
      scrollbar-gutter: stable both-edges;
    }
  }
}
</style>
